import { Box, Button, HStack, Image, RadioCard, Text } from '@chakra-ui/react';
import { PlatformEnum } from '@fluenceur/types';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { useEmailConnect } from '@/components/integrations/use-email-connect';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';

/**
 * Inline "connect your mailbox" affordance of a DISABLED email row: the existing
 * provider icons plus an underlined link opening a Gmail/Outlook menu (portalled
 * inside the dialog) that starts the shared OAuth redirect. No separate prompt
 * block: the state lives in the row itself. The label is surface-provided so the
 * affiliation and collab pickers keep their own copy.
 */
export const ConnectMailboxAffordance = ({ label }: { label: string }) => {
  const { connect, isConnecting } = useEmailConnect();
  return (
    // The row is a disabled radio (pointer-events: none); re-enable them here so
    // the connect link stays clickable, and stop the click bubbling to the label
    // (which would try to toggle the disabled radio).
    <HStack
      gap="2"
      flexShrink={0}
      pointerEvents="auto"
      onClick={(event) => event.stopPropagation()}
    >
      <HStack gap="1">
        <Image src="/images/integrations/gmail.svg" boxSize="4" alt="Gmail" />
        <Image src="/images/integrations/outlook.svg" boxSize="4" alt="Outlook" />
      </HStack>
      <MenuRoot positioning={{ placement: 'bottom-end' }}>
        <MenuTrigger asChild>
          <Button
            variant="plain"
            size="xs"
            h="auto"
            minH="0"
            px="0"
            fontSize="11.5px"
            fontWeight="medium"
            color="primary.600"
            textDecoration="underline"
            loading={isConnecting}
          >
            {label}
          </Button>
        </MenuTrigger>
        {/* portalled={false}: a body-portaled menu sits outside this dialog and
            gets dismissed by its interaction-outside handling — the same pattern
            the language toggle and deliverables menu already use. */}
        <MenuContent portalled={false}>
          <MenuItem value="gmail" onClick={() => void connect(PlatformEnum.Gmail)}>
            <Image src="/images/integrations/gmail.svg" boxSize="4" alt="" />
            Gmail
          </MenuItem>
          <MenuItem value="outlook" onClick={() => void connect(PlatformEnum.Outlook)}>
            <Image src="/images/integrations/outlook.svg" boxSize="4" alt="" />
            Outlook
          </MenuItem>
        </MenuContent>
      </MenuRoot>
    </HStack>
  );
};

/** One selectable channel row of a {@link ChannelPicker}, keyed by its value. */
export interface ChannelPickerRow<T extends string> {
  value: T;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  disabled?: boolean;
  /** Right-aligned slot (e.g. the connect affordance of a disabled email row). */
  trailing?: ReactNode;
}

/** One selectable channel row: radio, channel icon, title + one-clause
 * description. Checked/disabled styling comes from the theme's RadioCard recipe
 * (borders + background contrast only); the disabled treatment (no opacity dim,
 * recessed fill, muted title + indicator) is global to the recipe. */
const ChannelRow = <T extends string>({
  value,
  icon: Icon,
  title,
  description,
  disabled,
  trailing,
}: ChannelPickerRow<T>) => (
  <RadioCard.Item value={value} disabled={disabled} rounded="lg" w="full">
    <RadioCard.ItemHiddenInput />
    <RadioCard.ItemControl px="3.5" py="2.5" alignItems="center" gap="3">
      <RadioCard.ItemIndicator />
      <Text color="fg.muted" display="inline-flex" flex="none">
        <Icon width={15} height={15} />
      </Text>
      <Box flex="1" minW="0">
        <RadioCard.ItemText fontSize="13px" fontWeight="semibold">
          {title}
        </RadioCard.ItemText>
        <Text fontSize="11.5px" color="fg.muted" lineHeight="1.4">
          {description}
        </Text>
      </Box>
      {trailing}
    </RadioCard.ItemControl>
  </RadioCard.Item>
);

interface ChannelPickerProps<T extends string> {
  rows: ChannelPickerRow<T>[];
  /** Null leaves every row unchecked: the brand has to choose. */
  value: T | null;
  onChange: (value: T) => void;
}

/**
 * Generic delivery-channel radio group, shared by the sponsored wizard and the
 * affiliation channel step. Purely presentational and generic over the value
 * type: each surface builds its own rows (icons, copy, disabled state, trailing
 * affordance) from its capabilities and owns preselection.
 */
export const ChannelPicker = <T extends string>({
  rows,
  value,
  onChange,
}: ChannelPickerProps<T>) => (
  <RadioCard.Root
    value={value}
    onValueChange={({ value: next }) => {
      if (next) onChange(next as T);
    }}
    colorPalette="primary"
    gap="2"
  >
    {rows.map((row) => (
      <ChannelRow key={row.value} {...row} />
    ))}
  </RadioCard.Root>
);
